import React, { useState, useEffect, useCallback } from 'react';
import { useCMSAuth } from '../../context/CMSAuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  MoreHorizontal, 
  Shield, 
  ShieldCheck, 
  Edit2, 
  UserX,
  Key,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  AlertTriangle,
  MessageCircle,
  FileText,
  GraduationCap,
  Calendar,
  UserPlus,
  ClipboardList,
  Settings
} from 'lucide-react';

const API_URL = window.location.origin;

const ROLE_BADGES = {
  super_admin: { label: 'Super Admin', color: 'bg-red-500', icon: ShieldCheck },
  admin: { label: 'Admin', color: 'bg-purple-500', icon: ShieldCheck },
  content_approver: { label: 'Approver', color: 'bg-amber-500', icon: Shield },
  content_editor: { label: 'Editor', color: 'bg-blue-500', icon: Edit2 },
  contact_center: { label: 'Contact Center', color: 'bg-orange-500', icon: MessageCircle },
  viewer: { label: 'Viewer', color: 'bg-gray-500', icon: Eye }
};

// Fallback icon mapping for legacy module ids (new ids resolve via name string).
const MODULE_ICONS = {
  content: FileText,
  universities: GraduationCap,
  events: Calendar,
  leads: UserPlus,
  chat: MessageCircle,
  students: Users,
  applications: ClipboardList,
  settings: Settings,
  users: Shield
};

export const UserManagement = () => {
  const { token, user: currentUser, isAdmin, logout } = useCMSAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, by_role: {} });
  const [activeTab, setActiveTab] = useState('users');
  
  // Permission management state
  const [permissionRoles, setPermissionRoles] = useState([]);
  const [permissionChanges, setPermissionChanges] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'content_editor',
    department: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Filters
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (roleFilter && roleFilter !== 'all') params.append('role', roleFilter);
      
      const res = await fetch(`${API_URL}/api/admin/users?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users);
        setStats({ total: data.total, by_role: {} });
      } else if (res.status === 403) {
        toast.error('Only Super Admin can access user management');
      }
    } catch (e) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [token, roleFilter, logout]);

  const fetchRoles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRoles(data.roles);
        setModules(data.modules || []);
        // Initialize permission roles for editing
        setPermissionRoles(JSON.parse(JSON.stringify(data.roles)));
      }
    } catch (e) {
      console.error('Failed to fetch roles');
    }
  };

  const handlePermissionToggle = (roleId, moduleId) => {
    // Don't allow editing super_admin
    if (roleId === 'super_admin') return;
    
    setPermissionRoles(prev => prev.map(role => {
      if (role.id === roleId) {
        const currentPerms = role.permissions || [];
        const hasPermission = currentPerms.includes(moduleId);
        
        return {
          ...role,
          permissions: hasPermission
            ? currentPerms.filter(p => p !== moduleId)
            : [...currentPerms, moduleId]
        };
      }
      return role;
    }));
    setPermissionChanges(true);
  };

  // Toggle every module in a section ON if any is OFF, otherwise turn them all OFF.
  const handleSectionToggle = (roleId, sectionModuleIds) => {
    if (roleId === 'super_admin') return;
    setPermissionRoles(prev => prev.map(role => {
      if (role.id !== roleId) return role;
      const currentPerms = role.permissions || [];
      const hasAll = sectionModuleIds.every(id => currentPerms.includes(id));
      const nextPerms = hasAll
        ? currentPerms.filter(p => !sectionModuleIds.includes(p))
        : Array.from(new Set([...currentPerms, ...sectionModuleIds]));
      return { ...role, permissions: nextPerms };
    }));
    setPermissionChanges(true);
  };

  const savePermissions = async () => {
    setSavingPermissions(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/roles/permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ roles: permissionRoles })
      });
      
      if (res.ok) {
        toast.success('Permissions saved successfully');
        setPermissionChanges(false);
        fetchRoles(); // Refresh roles
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save permissions');
      }
    } catch (e) {
      toast.error('Error saving permissions');
    } finally {
      setSavingPermissions(false);
    }
  };

  const resetPermissions = () => {
    setPermissionRoles(JSON.parse(JSON.stringify(roles)));
    setPermissionChanges(false);
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers]);

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'content_editor',
      department: ''
    });
    setNewPassword('');
    setShowPassword(false);
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.name || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success(`User ${formData.email} created successfully`);
        setCreateDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to create user');
      }
    } catch (e) {
      toast.error('Failed to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setFormLoading(true);
    try {
      const updateData = {
        name: formData.name,
        role: formData.role,
        department: formData.department
      };
      
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('User updated successfully');
        setEditDialogOpen(false);
        resetForm();
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to update user');
      }
    } catch (e) {
      toast.error('Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setFormLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Password reset successfully');
        setResetPasswordDialogOpen(false);
        setNewPassword('');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (e) {
      toast.error('Failed to reset password');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedUser) return;
    
    setFormLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${selectedUser.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('User deactivated successfully');
        setDeactivateDialogOpen(false);
        fetchUsers();
      } else {
        toast.error(data.detail || 'Failed to deactivate user');
      }
    } catch (e) {
      toast.error('Failed to deactivate user');
    } finally {
      setFormLoading(false);
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      department: user.department || ''
    });
    setEditDialogOpen(true);
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.email?.toLowerCase().includes(query) ||
      u.name?.toLowerCase().includes(query) ||
      u.department?.toLowerCase().includes(query)
    );
  });

  const isSuperAdmin = isAdmin();

  if (!isSuperAdmin) {
    return (
      <AdminLayout>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <Shield className="w-16 h-16 text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">Access Restricted</h2>
          <p className="text-gray-500 mt-2">Only Super Admin can access user management.</p>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-6 h-6 text-ux-cyan" />
              User Management
            </h1>
            <p className="text-gray-500">Manage CMS users and role permissions</p>
          </div>
          {activeTab === 'users' && (
            <Button 
              className="bg-ux-cyan hover:bg-ux-cyan-dark"
              onClick={() => { resetForm(); setCreateDialogOpen(true); }}
              data-testid="create-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          )}
          {activeTab === 'permissions' && permissionChanges && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={resetPermissions}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Button 
                onClick={savePermissions} 
                disabled={savingPermissions}
                className="bg-green-600 hover:bg-green-700"
              >
                {savingPermissions ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="users" data-testid="users-tab">
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="permissions" data-testid="permissions-tab">
              <Shield className="w-4 h-4 mr-2" />
              Role Permissions
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold">{users.length}</p>
                </CardContent>
              </Card>
              {Object.entries(ROLE_BADGES).map(([roleId, roleInfo]) => {
                const count = users.filter(u => u.role === roleId).length;
                const Icon = roleInfo.icon;
                return (
                  <Card key={roleId}>
                    <CardContent className="p-4">
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Icon className="w-4 h-4" />
                        {roleInfo.label}s
                      </p>
                      <p className="text-2xl font-bold">{count}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-users-input"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <SelectValue placeholder="Filter by role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => (
                    <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => {
                    const roleInfo = ROLE_BADGES[user.role] || ROLE_BADGES.viewer;
                    const Icon = roleInfo.icon;
                    const isCurrentUser = user.id === currentUser?.id;
                    
                    return (
                      <TableRow key={user.id} className={isCurrentUser ? 'bg-blue-50' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${roleInfo.color} flex items-center justify-center text-white font-semibold`}>
                              {user.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-medium">
                                {user.name}
                                {isCurrentUser && (
                                  <span className="text-xs text-blue-600 ml-2">(You)</span>
                                )}
                              </p>
                              <p className="text-sm text-gray-500">{user.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${roleInfo.color} text-white`}>
                            <Icon className="w-3 h-3 mr-1" />
                            {roleInfo.label}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.department || '-'}</TableCell>
                        <TableCell>
                          {user.is_active !== false ? (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-red-600">
                              <XCircle className="w-4 h-4" />
                              Inactive
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.last_login 
                            ? new Date(user.last_login).toLocaleDateString() 
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => {
                                setSelectedUser(user);
                                setNewPassword('');
                                setResetPasswordDialogOpen(true);
                              }}>
                                <Key className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {!isCurrentUser && user.is_active !== false && (
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setDeactivateDialogOpen(true);
                                  }}
                                >
                                  <UserX className="w-4 h-4 mr-2" />
                                  Deactivate
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Role Descriptions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Role Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {roles.map(role => {
                const roleInfo = ROLE_BADGES[role.id] || ROLE_BADGES.viewer;
                const Icon = roleInfo.icon;
                return (
                  <div key={role.id} className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={`${roleInfo.color} text-white`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {role.name}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">{role.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          {/* Permissions Tab */}
          <TabsContent value="permissions" className="space-y-6 mt-6">
            {permissionChanges && (
              <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
                <AlertTriangle className="w-5 h-5" />
                <span>You have unsaved changes. Click "Save Changes" to apply them.</span>
              </div>
            )}

            {/* Permission Matrix */}
            <Card>
              <CardHeader>
                <CardTitle>Permission Matrix</CardTitle>
                <CardDescription>
                  Toggle module access for each role. Super Admin always has full access.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 font-medium text-gray-500">Module</th>
                        {permissionRoles.map(role => {
                          const roleInfo = ROLE_BADGES[role.id] || ROLE_BADGES.viewer;
                          const Icon = roleInfo.icon;
                          return (
                            <th key={role.id} className="text-center py-3 px-4 min-w-[120px]">
                              <div className="flex flex-col items-center gap-1">
                                <div className={`p-2 rounded-full ${roleInfo.color} text-white`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <span className="text-sm font-medium">{role.name}</span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Group modules by section, preserving backend order
                        const sections = [];
                        const seen = new Map();
                        modules.forEach(m => {
                          const sec = m.section || 'Other';
                          if (!seen.has(sec)) {
                            seen.set(sec, sections.length);
                            sections.push({ name: sec, items: [] });
                          }
                          sections[seen.get(sec)].items.push(m);
                        });

                        return sections.map(section => (
                          <React.Fragment key={section.name}>
                            {/* Section header row */}
                            <tr className="bg-slate-100/70 border-t border-b">
                              <td className="py-2 px-4">
                                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                                  {section.name}
                                </div>
                              </td>
                              {permissionRoles.map(role => {
                                const sectionIds = section.items.map(i => i.id);
                                const isSuper = role.id === 'super_admin' || (role.permissions || []).includes('all');
                                const checkedCount = isSuper
                                  ? sectionIds.length
                                  : sectionIds.filter(id => (role.permissions || []).includes(id)).length;
                                const allChecked = checkedCount === sectionIds.length;
                                const someChecked = checkedCount > 0 && !allChecked;
                                return (
                                  <td key={`${role.id}-${section.name}-header`} className="text-center py-2 px-4">
                                    <button
                                      type="button"
                                      onClick={() => handleSectionToggle(role.id, sectionIds)}
                                      disabled={isSuper}
                                      className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                                        isSuper
                                          ? 'opacity-40 cursor-not-allowed border-slate-300'
                                          : allChecked
                                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                                            : someChecked
                                              ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'
                                              : 'bg-white border-slate-300 text-slate-500 hover:bg-slate-50'
                                      }`}
                                      title={allChecked ? 'Disable all in section' : 'Enable all in section'}
                                      data-testid={`section-toggle-${role.id}-${section.name.replace(/\s+/g, '-').toLowerCase()}`}
                                    >
                                      {checkedCount}/{sectionIds.length}
                                    </button>
                                  </td>
                                );
                              })}
                            </tr>
                            {/* Item rows */}
                            {section.items.map(module => {
                              const ModuleIcon = MODULE_ICONS[module.id] || FileText;
                              return (
                                <tr key={module.id} className="border-b hover:bg-gray-50">
                                  <td className="py-3 px-4 pl-8">
                                    <div className="flex items-center gap-3">
                                      <ModuleIcon className="w-4 h-4 text-gray-400" />
                                      <div>
                                        <div className="font-medium text-sm">{module.name}</div>
                                        <div className="text-xs text-gray-500">{module.description}</div>
                                      </div>
                                    </div>
                                  </td>
                                  {permissionRoles.map(role => {
                                    const hasPermission = role.id === 'super_admin' ||
                                      (role.permissions || []).includes('all') ||
                                      (role.permissions || []).includes(module.id);
                                    const isDisabled = role.id === 'super_admin';
                                    return (
                                      <td key={`${role.id}-${module.id}`} className="text-center py-3 px-4">
                                        <div className="flex justify-center">
                                          <Switch
                                            checked={hasPermission}
                                            onCheckedChange={() => handlePermissionToggle(role.id, module.id)}
                                            disabled={isDisabled}
                                            className={isDisabled ? 'opacity-50' : ''}
                                            data-testid={`perm-${role.id}-${module.id}`}
                                          />
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Role Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {permissionRoles.map(role => {
                const roleInfo = ROLE_BADGES[role.id] || ROLE_BADGES.viewer;
                const Icon = roleInfo.icon;
                const permCount = role.id === 'super_admin' ? 'All' : (role.permissions || []).length;
                
                return (
                  <Card key={role.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${roleInfo.color} text-white`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{role.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                          <div className="mt-3">
                            <Badge variant="secondary">
                              {permCount} {typeof permCount === 'number' ? 'modules' : 'access'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new CMS user with specific role and permissions.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Full name"
                  data-testid="create-user-name"
                />
              </div>
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="user@unixperts.com"
                  data-testid="create-user-email"
                />
              </div>
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Minimum 6 characters"
                    data-testid="create-user-password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Role *</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({...formData, role: v})}
                >
                  <SelectTrigger data-testid="create-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Department (Optional)</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="e.g., Marketing, Content, Operations"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-ux-cyan hover:bg-ux-cyan-dark"
                onClick={handleCreateUser}
                disabled={formLoading}
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details and role.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Email</Label>
                <Input value={formData.email} disabled className="bg-gray-100" />
              </div>
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({...formData, role: v})}
                  disabled={selectedUser?.id === currentUser?.id}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedUser?.id === currentUser?.id && (
                  <p className="text-xs text-amber-600 mt-1">You cannot change your own role</p>
                )}
              </div>
              <div>
                <Label>Department</Label>
                <Input
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  placeholder="e.g., Marketing, Content, Operations"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-ux-cyan hover:bg-ux-cyan-dark"
                onClick={handleUpdateUser}
                disabled={formLoading}
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Set a new password for {selectedUser?.name}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                className="bg-ux-cyan hover:bg-ux-cyan-dark"
                onClick={handleResetPassword}
                disabled={formLoading || !newPassword}
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reset Password
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate User Dialog */}
        <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-600">Deactivate User</DialogTitle>
              <DialogDescription>
                Are you sure you want to deactivate {selectedUser?.name}? They will no longer be able to access the CMS.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-800">
                  <strong>Warning:</strong> This user will be immediately logged out and unable to access any CMS features.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={handleDeactivateUser}
                disabled={formLoading}
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Deactivate User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
